/**
 * Component Enhancement Dialog
 * Allows players to spend $ISD to upgrade component star ratings (1★ to 5★).
 * Shows current stars, rarity, base vs effective stats, cost, success rate, pity progress.
 */

import { EventBus } from '../../utils/EventBus';
import {
  createDialogChrome,
  Colors, CSSColors, Fonts, FontSize, Space,
} from './GameUITheme';
import { getApiBaseUrl } from '../../utils/ApiConfig';
import { isMobile } from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

const API_URL = getApiBaseUrl();

// Rarity colors
const RARITY_COLORS: Record<string, number> = {
  common: 0xcccccc,
  uncommon: 0x4ade80,
  rare: 0x60a5fa,
  epic: 0xa855f7,
  legendary: 0xffd700,
};

const RARITY_CSS: Record<string, string> = {
  common: '#cccccc',
  uncommon: '#4ade80',
  rare: '#60a5fa',
  epic: '#a855f7',
  legendary: '#FFD700',
};

const STAR_MULTIPLIERS: Record<number, number> = { 1: 1.0, 2: 1.15, 3: 1.35, 4: 1.6, 5: 2.0 };

interface EnhancementInfoData {
  starRating: number;
  rarity: string;
  pityCount: number;
  nextCost: number | null;
  successRate: number | null;
  pityThreshold: number | null;
  starMultiplier: number;
}

export class ComponentEnhancementDialog {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private dw: number;
  private dh: number;
  private visible: boolean = false;
  private currentComponentId: string | null = null;
  private currentComponentName: string = '';
  private currentCategory: string = '';
  private enhancing: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const mobile = isMobile(scene);
    this.dw = mobile ? 340 : 520;
    this.dh = mobile ? 540 : 520;
    const { width, height } = scene.cameras.main;
    this.container = scene.add.container(width / 2 - this.dw / 2, height / 2 - this.dh / 2);
    this.container.setDepth(2100);
    this.container.setVisible(false);
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('crimsonmandate_token');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }

  private async fetchEnhancementInfo(instanceId: string): Promise<EnhancementInfoData | null> {
    try {
      const response = await fetch(`${API_URL}/components/${instanceId}/enhancement-info`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  private async doEnhance(instanceId: string): Promise<any> {
    const response = await fetch(`${API_URL}/components/enhance`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ componentInstanceId: instanceId }),
    });
    return await response.json();
  }

  private async buildDialog(info: EnhancementInfoData): Promise<void> {
    this.container.removeAll(true);

    createDialogChrome(this.scene, this.container, this.dw, this.dh, {
      title: I18n.t('dialog.component_enhancement'),
      onClose: () => this.hide(),
    });

    const rarity = info.rarity || 'common';
    const rarityColor = RARITY_CSS[rarity] || '#cccccc';
    let y = 64;

    // Component name + rarity
    const nameText = this.scene.add.text(this.dw / 2, y, this.currentComponentName, {
      fontSize: FontSize.lg,
      color: rarityColor,
      fontFamily: Fonts.heading,
      align: 'center',
    });
    nameText.setOrigin(0.5, 0);
    this.container.add(nameText);
    y += 32;

    // Category badge
    const catText = this.scene.add.text(this.dw / 2, y, `[ ${this.currentCategory.toUpperCase()} ]`, {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    catText.setOrigin(0.5, 0);
    this.container.add(catText);
    y += 28;

    // Star display
    const stars = info.starRating;
    const starStr = '★'.repeat(stars) + '☆'.repeat(5 - stars);
    const starText = this.scene.add.text(this.dw / 2, y, starStr, {
      fontSize: '36px',
      color: stars >= 5 ? '#FFD700' : rarityColor,
      fontFamily: Fonts.heading,
    });
    starText.setOrigin(0.5, 0);
    this.container.add(starText);
    y += 50;

    // Rarity label
    const rarityLabel = this.scene.add.text(this.dw / 2, y, rarity.toUpperCase(), {
      fontSize: FontSize.sm,
      color: rarityColor,
      fontFamily: Fonts.heading,
    });
    rarityLabel.setOrigin(0.5, 0);
    this.container.add(rarityLabel);
    y += 30;

    // Current multiplier
    const multText = this.scene.add.text(this.dw / 2, y, `Star Multiplier: ${info.starMultiplier.toFixed(2)}x`, {
      fontSize: FontSize.md,
      color: '#ffffff',
      fontFamily: Fonts.body,
    });
    multText.setOrigin(0.5, 0);
    this.container.add(multText);
    y += 36;

    // Separator
    const sep = this.scene.add.graphics();
    sep.lineStyle(1, Colors.cyan, 0.15);
    sep.lineBetween(Space.lg, y, this.dw - Space.lg, y);
    this.container.add(sep);
    y += 16;

    if (stars >= 5) {
      // Max star rating
      const maxText = this.scene.add.text(this.dw / 2, y + 30, 'MAXIMUM STAR RATING REACHED', {
        fontSize: FontSize.md,
        color: '#FFD700',
        fontFamily: Fonts.heading,
      });
      maxText.setOrigin(0.5, 0);
      this.container.add(maxText);
    } else {
      // Enhancement details
      const nextMult = STAR_MULTIPLIERS[(stars + 1)] || 1.0;

      // Cost
      const costText = this.scene.add.text(Space.xl, y, `Cost: ${info.nextCost} $ISD`, {
        fontSize: FontSize.md,
        color: CSSColors.cyan,
        fontFamily: Fonts.body,
      });
      this.container.add(costText);
      y += 26;

      // Success rate
      const rateStr = info.successRate !== null ? `${Math.round(info.successRate * 100)}%` : 'N/A';
      const rateText = this.scene.add.text(Space.xl, y, `Success Rate: ${rateStr}`, {
        fontSize: FontSize.md,
        color: '#ffffff',
        fontFamily: Fonts.body,
      });
      this.container.add(rateText);
      y += 26;

      // Next multiplier
      const nextMultText = this.scene.add.text(Space.xl, y, `Next Multiplier: ${info.starMultiplier.toFixed(2)}x → ${nextMult.toFixed(2)}x`, {
        fontSize: FontSize.md,
        color: '#4ade80',
        fontFamily: Fonts.body,
      });
      this.container.add(nextMultText);
      y += 26;

      // Pity progress
      if (info.pityThreshold !== null) {
        const pityPct = Math.min(1, info.pityCount / info.pityThreshold);

        const pityLabel = this.scene.add.text(Space.xl, y, `Pity Progress: ${info.pityCount} / ${info.pityThreshold}`, {
          fontSize: FontSize.sm,
          color: CSSColors.textMuted,
          fontFamily: Fonts.body,
        });
        this.container.add(pityLabel);
        y += 22;

        // Progress bar
        const barW = this.dw - Space.xl * 2;
        const barH = 12;
        const barBg = this.scene.add.rectangle(Space.xl, y, barW, barH, 0x1a1a3e);
        barBg.setOrigin(0, 0);
        barBg.setStrokeStyle(1, Colors.cyan, 0.2);
        this.container.add(barBg);

        if (pityPct > 0) {
          const barFill = this.scene.add.rectangle(Space.xl, y, barW * pityPct, barH, RARITY_COLORS[rarity] || Colors.cyan);
          barFill.setOrigin(0, 0);
          this.container.add(barFill);
        }
        y += 24;

        if (info.pityCount + 1 >= info.pityThreshold) {
          const pityReady = this.scene.add.text(this.dw / 2, y, 'GUARANTEED SUCCESS ON NEXT ATTEMPT!', {
            fontSize: FontSize.sm,
            color: '#FFD700',
            fontFamily: Fonts.heading,
          });
          pityReady.setOrigin(0.5, 0);
          this.container.add(pityReady);
          y += 24;
        }
      }

      // Enhance button
      y = this.dh - 70;
      const btnW = 200;
      const btnH = 44;
      const btnX = this.dw / 2 - btnW / 2;
      const enhanceBtn = this.scene.add.rectangle(btnX, y, btnW, btnH, Colors.cyan, 0.2);
      enhanceBtn.setOrigin(0, 0);
      enhanceBtn.setStrokeStyle(2, Colors.cyan, 0.6);
      enhanceBtn.setInteractive({ useHandCursor: true });

      const btnText = this.scene.add.text(btnX + btnW / 2, y + btnH / 2, `ENHANCE (${info.nextCost} $ISD)`, {
        fontSize: FontSize.md,
        color: CSSColors.cyan,
        fontFamily: Fonts.heading,
      });
      btnText.setOrigin(0.5);

      enhanceBtn.on('pointerover', () => enhanceBtn.setFillStyle(Colors.cyan, 0.35));
      enhanceBtn.on('pointerout', () => enhanceBtn.setFillStyle(Colors.cyan, 0.2));
      enhanceBtn.on('pointerdown', () => this.onEnhanceClick());

      this.container.add(enhanceBtn);
      this.container.add(btnText);
    }
  }

  private async onEnhanceClick(): Promise<void> {
    if (this.enhancing || !this.currentComponentId) return;
    this.enhancing = true;

    try {
      const result = await this.doEnhance(this.currentComponentId);

      if (result.success) {
        // Success animation
        this.showResult(true, result.newStarRating, result.newRarity, result.pityTriggered);
      } else if (result.error) {
        this.showResult(false);
        this.showNotification(result.error, '#ff6b6b');
      } else {
        // Enhancement failed (but ISD was spent)
        this.showResult(false);
        this.showNotification('Enhancement failed! Try again.', '#ff6b6b');
      }

      // Refresh the dialog
      setTimeout(() => this.refreshDialog(), 1500);
    } catch (err) {
      this.showNotification('Enhancement error', '#ff6b6b');
    } finally {
      this.enhancing = false;
    }
  }

  private showResult(success: boolean, newStars?: number, newRarity?: string, pityTriggered?: boolean): void {
    if (success) {
      const msg = pityTriggered
        ? `PITY TRIGGERED! ${newStars}★ ${(newRarity || '').toUpperCase()}!`
        : `SUCCESS! ${newStars}★ ${(newRarity || '').toUpperCase()}!`;
      this.showNotification(msg, '#FFD700');

      // Shimmer effect
      const shimmer = this.scene.add.rectangle(0, 0, this.dw, this.dh, RARITY_COLORS[newRarity || 'common'] || 0xffd700, 0);
      shimmer.setOrigin(0, 0);
      this.container.add(shimmer);
      this.scene.tweens.add({
        targets: shimmer,
        alpha: { from: 0.4, to: 0 },
        duration: 800,
        ease: 'Power2',
        onComplete: () => shimmer.destroy(),
      });
    } else {
      // Shake effect
      const startX = this.container.x;
      this.scene.tweens.add({
        targets: this.container,
        x: { from: startX - 8, to: startX + 8 },
        duration: 50,
        yoyo: true,
        repeat: 3,
        onComplete: () => { this.container.x = startX; },
      });
    }
  }

  private showNotification(message: string, color: string): void {
    const notification = this.scene.add.text(this.dw / 2, -20, message, {
      fontSize: FontSize.sm,
      color: color,
      fontFamily: Fonts.body,
      backgroundColor: '#0D0D1F',
      padding: { x: 12, y: 8 },
    });
    notification.setOrigin(0.5);
    notification.setAlpha(0);
    this.container.add(notification);

    this.scene.tweens.add({
      targets: notification,
      alpha: 1,
      duration: 200,
      yoyo: true,
      hold: 2000,
      onComplete: () => notification.destroy(),
    });
  }

  private async refreshDialog(): Promise<void> {
    if (!this.currentComponentId) return;
    const info = await this.fetchEnhancementInfo(this.currentComponentId);
    if (info) {
      await this.buildDialog(info);
    }
  }

  public async show(componentInstanceId: string, componentName: string, category: string): Promise<void> {
    this.currentComponentId = componentInstanceId;
    this.currentComponentName = componentName;
    this.currentCategory = category;
    this.visible = true;

    const info = await this.fetchEnhancementInfo(componentInstanceId);
    if (!info) {
      this.showNotification('Failed to load enhancement info', '#ff6b6b');
      return;
    }

    await this.buildDialog(info);
    this.container.setVisible(true);
    EventBus.on('close-topmost-dialog', this.hide, this);
  }

  public hide(): void {
    EventBus.off('close-topmost-dialog', this.hide, this);
    this.visible = false;
    this.currentComponentId = null;
    this.scene.input.setDefaultCursor('default');

    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.container.setVisible(false);
        this.container.setAlpha(1);
      },
    });
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public destroy(): void {
    this.container.destroy();
  }
}
